import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Text } from '@radix-ui/themes';
import { ChevronDownIcon, ChevronUpIcon } from '@/components/icons';

interface HeaderNavProps {
  className?: string;
  openMenu: string | null;
  onMenuEnter: (menu: string) => void;
}

// "Experiences" is temporarily hidden from the navbar — keep the entry
// commented so it's easy to restore once the section is ready to ship.
//
// Spec 022 — "Assets" is the entry point into /library/assets. The
// MegaMenuPanel for it shows the asset-bearing category list; clicking
// any category navigates to /library/assets?category=<slug>.
const sections = [
  { key: 'brands', label: 'Brands' },
  { key: 'products', label: 'Products' },
  { key: 'assets', label: 'Assets' },
  // { key: 'experiences', label: 'Experiences' },
] as const;

export function HeaderNav({ className, openMenu, onMenuEnter }: HeaderNavProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const section = searchParams.get('section');

  const isActive = (s: string) => section === s;

  const handleSectionClick = useCallback((sectionName: string) => {
    // Spec 022 — Assets is its own top-level surface, not a section
    // filter on the guideline library.
    if (sectionName === 'assets') {
      navigate('/library/assets');
      return;
    }
    navigate(`/library?section=${sectionName}`);
  }, [navigate]);

  // Hover indicator: a 2px orange underline that slides between sections
  // and sits along the pill's bottom border, overlapping it. Width tracks
  // the label width so the underline reads as a tab indicator rather than
  // a centered dot.
  const labelRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const [indicator, setIndicator] = useState<{ x: number; width: number; visible: boolean }>(
    { x: 0, width: 0, visible: false }
  );

  // Hover wins while the user is exploring; otherwise the underline rests
  // under whichever section the URL says is active. Drops to invisible
  // when neither is set (e.g. on the homepage / non-library routes).
  const activeKey = openMenu ?? section ?? null;

  useLayoutEffect(() => {
    if (!activeKey) {
      setIndicator((s) => ({ ...s, visible: false }));
      return;
    }
    const idx = sections.findIndex((s) => s.key === activeKey);
    if (idx < 0) {
      setIndicator((s) => ({ ...s, visible: false }));
      return;
    }
    const labelEl = labelRefs.current[idx];
    if (!labelEl) return;

    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const navEl = labelEl.closest('nav') as HTMLElement | null;
      if (!navEl) return;
      const navRect = navEl.getBoundingClientRect();
      const labelRect = labelEl.getBoundingClientRect();
      const x = labelRect.left - navRect.left;
      setIndicator({ x, width: labelRect.width, visible: true });
    };

    // Defer two animation frames so any pending layout (esp. after a
    // font swap) has actually been applied before we read offsets.
    const measureNextFrame = () => {
      requestAnimationFrame(() => requestAnimationFrame(measure));
    };

    // Initial measurement (may be against fallback font on a hard refresh).
    measure();

    // Geist Mono / PP Neue Montreal arrive asynchronously on a cold load.
    // Re-measure both when the load promise resolves AND when the
    // browser dispatches `loadingdone` — `ready` only fires once for the
    // initial set, but `loadingdone` covers later font activations too.
    let fontsHandler: (() => void) | undefined;
    if ('fonts' in document) {
      fontsHandler = () => measureNextFrame();
      document.fonts.addEventListener('loadingdone', fontsHandler);
      document.fonts.ready.then(() => {
        if (!cancelled) measureNextFrame();
      });
    }

    // Safety nets: in case font activation slips past every other signal,
    // re-check at 100ms and 500ms after mount.
    const t1 = setTimeout(measureNextFrame, 100);
    const t2 = setTimeout(measureNextFrame, 500);

    // Catch any subsequent label resize (font swap, viewport change,
    // section rename) so the dot stays glued to its target.
    const ro = new ResizeObserver(measure);
    ro.observe(labelEl);
    // Also observe the parent nav — in flex layouts a sibling resize can
    // shift the active label without altering its own bounding box.
    const navEl = labelEl.closest('nav') as HTMLElement | null;
    if (navEl) ro.observe(navEl);

    window.addEventListener('resize', measure);

    return () => {
      cancelled = true;
      ro.disconnect();
      window.removeEventListener('resize', measure);
      clearTimeout(t1);
      clearTimeout(t2);
      if (fontsHandler) {
        document.fonts.removeEventListener('loadingdone', fontsHandler);
      }
    };
  }, [activeKey]);

  return (
    <nav className={`flex items-center gap-8 relative h-full ${className ?? ''}`}>
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          // Sit on top of the pill's 1px bottom border so the line
          // overlaps it. Indicator is 2px tall: 1px above the border
          // line, 1px directly on it.
          bottom: '-1px',
          transform: `translateX(${indicator.x}px)`,
          width: `${indicator.width}px`,
          height: '2px',
          opacity: indicator.visible ? 1 : 0,
          background: '#ff5310',
          pointerEvents: 'none',
          zIndex: 1,
          transition:
            'transform 320ms cubic-bezier(0.22, 1, 0.36, 1), ' +
            'width 320ms cubic-bezier(0.22, 1, 0.36, 1), ' +
            'opacity 200ms ease-out',
          willChange: 'transform, width',
        }}
      />
      {sections.map(({ key, label }, i) => (
        <div
          key={key}
          className="flex items-center relative"
          style={{ zIndex: 1 }}
          onMouseEnter={() => onMenuEnter(key)}
        >
          <Button
            variant="ghost"
            size="2"
            style={{
              color: isActive(key) ? 'var(--accent-11)' : 'var(--gray-12)',
              margin: 0,
              background: 'transparent',
              // Radix size-2 ghost buttons default to ~4 px top/bottom
              // padding. Bump the top to 16 px per spec — the larger
              // top inset gives the label more breathing room from the
              // header's top edge. Bottom stays at the Radix default
              // so the active-section indicator still meets the row's
              // 1 px bottom stroke at the expected y position.
              paddingTop: 16,
            }}
            onClick={() => handleSectionClick(key)}
          >
            <Text
              weight="medium"
              style={{
                fontSize: '14px',
                lineHeight: '18px',
                fontFamily:
                  "'Geist Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
              ref={(el: HTMLSpanElement | null) => { labelRefs.current[i] = el; }}
            >
              {label}
            </Text>
            {openMenu === key
              ? <ChevronUpIcon width="14" height="14" />
              : <ChevronDownIcon width="14" height="14" />
            }
          </Button>
        </div>
      ))}
    </nav>
  );
}
